import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatCPF(cpf) {
  if (!cpf) return '';
  const cleaned = cpf.replace(/\D/g, '');
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

export function validateCPF(cpf) {
  const cleaned = cpf.replace(/\D/g, '');
  
  if (cleaned.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;
  
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned[i]) * (10 - i);
  }
  let digit1 = (sum * 10) % 11;
  if (digit1 === 10) digit1 = 0;
  if (digit1 !== parseInt(cleaned[9])) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned[i]) * (11 - i);
  }
  let digit2 = (sum * 10) % 11;
  if (digit2 === 10) digit2 = 0;
  if (digit2 !== parseInt(cleaned[10])) return false;
  
  return true;
}

/**
 * Converte string de data para Date seguro no timezone local.
 * Strings "YYYY-MM-DD" (sem hora) são interpretadas como UTC meia-noite,
 * causando shift de -1 dia em fusos negativos. Esta função corrige isso.
 */
function safeParseDate(dateString) {
  if (!dateString) return null;
  // Date-only: "2026-02-28" → append T12:00:00 to avoid timezone day shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return new Date(dateString + 'T12:00:00');
  }
  return new Date(dateString);
}

export function formatDate(dateString) {
  if (!dateString) return '-';
  const date = safeParseDate(dateString);
  return date.toLocaleDateString('pt-BR', { timeZone: 'America/Manaus' });
}

export function formatDateTime(dateString) {
  if (!dateString) return '-';
  // For full datetime strings, use as-is (they have timezone info)
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', { timeZone: 'America/Manaus' });
}

/**
 * Retorna a data de hoje no timezone de Manaus (UTC-4) no formato YYYY-MM-DD
 */
export function todayLocal() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Manaus' });
}

/**
 * Exibe CPF completo ou mascarado conforme permissão
 */
export function displayCPF(cpf, canViewFull = false) {
  if (!cpf) return '-';
  const clean = cpf.replace(/\D/g, '');
  if (canViewFull) {
    return formatCPF(cpf);
  }
  if (clean.length >= 3) {
    return `***.***.*${clean.slice(-4, -2)}-${clean.slice(-2)}`;
  }
  return '***.***.***-**';
}

export function calculateDaysAdmitted(admissionDate) {
  if (!admissionDate) return 0;
  const admission = safeParseDate(admissionDate);
  const today = new Date();
  const diffTime = Math.abs(today - admission);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function getDaysAlertLevel(days) {
  if (days >= 60) return 'critical';
  if (days >= 30) return 'warning';
  return 'normal';
}

export function getDaysBucket(days) {
  if (days <= 5) return '0-5';
  if (days <= 15) return '6-15';
  if (days <= 30) return '16-30';
  return '>30';
}

export const STATUS_LABELS = {
  admissao_pendente: 'Admissão Pendente',
  ativo_estavel: 'Ativo - Estável',
  ativo_atencao: 'Ativo - Atenção',
  alta_planejada: 'Alta Planejada',
  alta_efetivada: 'Alta Efetivada'
};

export const STATUS_COLORS = {
  admissao_pendente: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  ativo_estavel: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  ativo_atencao: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  alta_planejada: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  alta_efetivada: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
};

export const TASK_TYPE_LABELS = {
  visita: 'Visita',
  imagem: 'Imagem',
  laboratorial: 'Laboratorial',
  procedimento: 'Procedimento',
  pendencia: 'Pendência Adm.'
};

// Task types that show dark purple halo
export const TASK_TYPES_WITH_HALO = ['imagem', 'procedimento'];

export const TASK_STATUS_LABELS = {
  a_fazer: 'A Fazer',
  em_andamento: 'Em Andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada'
};

export const PRIORITY_LABELS = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta'
};

export const PRIORITY_COLORS = {
  baixa: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  media: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  alta: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
};

export const PROFILE_LABELS = {
  master_admin: 'Master Admin',
  admin: 'Administrador',
  aux_adm: 'Aux. Adm',
  gestor: 'Gestor',
  operador: 'Operador',
  coordenador: 'Coordenador',
  profissional: 'Profissional',
  tecnico: 'Técnico de Enfermagem',
  recepcionista: 'Recepcionista',
  financeiro: 'Financeiro',
  medico: 'Médico',
  enfermeiro: 'Enfermeiro(a)',
  recepcao: 'Recepção',
  controlista_estoque: 'Controlista de Estoque',
  fisioterapeuta: 'Fisioterapeuta',
  fonoaudiologo: 'Fonoaudiólogo',
  nutricionista: 'Nutricionista',
  psicologo: 'Psicólogo',
  clinico: 'Clínico'
};

export const ALTA_MOTIVO_LABELS = {
  alta_clinica: 'Alta Clínica',
  alta_administrativa: 'Alta Administrativa',
  transferencia: 'Transferência',
  obito: 'Óbito',
  desistencia: 'Desistência' // Legacy, mapeado para alta_administrativa
};

// Origem v2: Grupo + Subgrupo
export const ORIGEM_GRUPOS = {
  HGR: 'HGR',
  HMI: 'HMI',
  PACS: 'PACS',
};

export const HGR_SUBGRUPOS = {
  completar_atb: 'Completar ATB',
  cardiologia: 'Cardiologia',
  paliativo: 'Paliativo',
  clinico: 'Clínico',
  cirurgia: 'Cirurgia',
};

export const ORIGEM_GRUPO_COLORS = {
  HGR: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  HMI: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  PACS: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  nao_informado: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

export const HGR_SUBGRUPO_COLORS = {
  completar_atb: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  cardiologia: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  paliativo: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  clinico: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  cirurgia: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
};

export function formatOrigem(grupo, subgrupo) {
  if (grupo === 'HGR' && subgrupo) {
    return `HGR — ${HGR_SUBGRUPOS[subgrupo] || subgrupo}`;
  }
  return grupo || 'Não Informado';
}

export function getOrigemColor(grupo, subgrupo) {
  if (grupo === 'HGR' && subgrupo) {
    return HGR_SUBGRUPO_COLORS[subgrupo] || ORIGEM_GRUPO_COLORS.HGR;
  }
  return ORIGEM_GRUPO_COLORS[grupo] || ORIGEM_GRUPO_COLORS.nao_informado;
}

// Legacy ORIGEM constants (kept for backward compatibility during migration)
export const ORIGEM_LABELS = {
  cardiologia: 'Cardiologia',
  paliativo: 'Paliativo',
  clinico: 'Clínico',
  cirurgia: 'Cirurgia',
  completar_atb: 'Completar ATB',
  pacs: 'PACS',
  hmi: 'HMI',
  HGR: 'HGR',
  HMI: 'HMI',
  PACS: 'PACS',
  nao_informado: 'Não Informado'
};

export const ORIGEM_COLORS = {
  cardiologia: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  paliativo: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  clinico: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  pacs: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  hmi: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  cirurgia: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  completar_atb: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400',
  HGR: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  HMI: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  PACS: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  nao_informado: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
};

export const LOS_BUCKET_LABELS = {
  '0-5': '0-5 dias',
  '6-15': '6-15 dias',
  '16-30': '16-30 dias',
  '>30': '> 30 dias'
};

export const LOS_BUCKET_COLORS = {
  '0-5': { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-500' },
  '6-15': { bg: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-500' },
  '16-30': { bg: 'bg-orange-500', text: 'text-orange-700', light: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-500' },
  '>30': { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-500' }
};

// CEP lookup via ViaCEP
export async function buscarCEP(cep) {
  const cepLimpo = cep.replace(/\D/g, '');
  if (cepLimpo.length !== 8) {
    return { error: 'CEP deve ter 8 dígitos' };
  }
  
  try {
    const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
    const data = await response.json();
    
    if (data.erro) {
      return { error: 'CEP não encontrado' };
    }
    
    return {
      logradouro: data.logradouro || '',
      bairro: data.bairro || '',
      cidade: data.localidade || '',
      uf: data.uf || ''
    };
  } catch (error) {
    return { error: 'Erro ao buscar CEP. Preencha manualmente.' };
  }
}
